import { AtomErrors } from "./AtomErrors";
import { AuthErrors } from "./AuthErrors";
import { InternalErrors } from "./InternalErrors";
import { PrismaErrors } from "./PrismaErrors";
import { UserServiceErrors } from "./UserServiceErrors";

/**
 * What kind of failure an error is, the same on every transport. The backend
 * derives it from the tables below and sends it as `errorClass`; it decides
 * the severity and the sentence a user reads.
 */
export const ERROR_CLASSES = [
  "permission",
  "validation",
  "conflict",
  "busy",
  "not-found",
  "rule",
  "unavailable",
  "auth",
  "internal",
] as const;

export type ErrorClass = (typeof ERROR_CLASSES)[number];

const errorClasses = new Set<string>(ERROR_CLASSES);

export function isErrorClass(value: unknown): value is ErrorClass {
  return typeof value === "string" && errorClasses.has(value);
}

/** Every code of the framework catalogs. A new member needs a class here. */
type CatalogErrorCode =
  | AtomErrors
  | InternalErrors
  | AuthErrors
  | UserServiceErrors
  | PrismaErrors;

/** `debugInfos.errorCode` of a rejected GraphQL operation or reducer input. */
const VALIDATION_ERROR_CODE = "ERR-VAL-001";

/**
 * A code that is thrown both for a mistake of the caller and for a defect of
 * the server stays `internal`: it keeps the error level and the error
 * tracking it has.
 */
const CLASS_BY_CATALOG_CODE: Record<CatalogErrorCode, ErrorClass> = {
  [AtomErrors.CannotProvideAtom]: "not-found",
  [AtomErrors.AtomIsNotRespawnable]: "conflict",
  [AtomErrors.InternalError]: "internal",
  [AtomErrors.FailedToGetLock]: "busy",
  [AtomErrors.PersistenceServiceCriticalError]: "internal",
  [AtomErrors.UniqueConstraintViolation]: "conflict",
  [AtomErrors.InsufficientPermission]: "permission",
  [AtomErrors.DeadLock]: "busy",
  [AtomErrors.FoundAtomMoved]: "busy",
  [AtomErrors.LockTimedOut]: "busy",

  [InternalErrors.NoActingUser]: "internal",
  [InternalErrors.MissingContextEntry]: "internal",
  [InternalErrors.InsufficientContext]: "internal",
  [InternalErrors.CannotSpawnVirtualAtom]: "internal",
  [InternalErrors.CannotSpawnWithoutLocking]: "internal",
  [InternalErrors.CannotCommitWithoutLocking]: "internal",
  [InternalErrors.CannotCommitTwice]: "internal",
  [InternalErrors.CannotDirectlyChangeVirtualAtoms]: "internal",
  [InternalErrors.InfiniteLoop]: "internal",
  [InternalErrors.TryingToCommitFailedTransaction]: "internal",
  [InternalErrors.HashMismatch]: "conflict",
  [InternalErrors.MissingPayload]: "internal",
  [InternalErrors.ErrorDuringTransformation]: "internal",
  [InternalErrors.NoLockerSet]: "internal",
  [InternalErrors.LockerNotFound]: "internal",
  [InternalErrors.MissingModuleRef]: "internal",
  [InternalErrors.ContextChanged]: "conflict",
  [InternalErrors.InsufficientPermissions]: "permission",
  [InternalErrors.Persistence]: "internal",
  [InternalErrors.Configuration]: "internal",
  [InternalErrors.MissingDependency]: "internal",
  [InternalErrors.NotFound]: "not-found",
  [InternalErrors.Validation]: "validation",
  [InternalErrors.UnsupportedOperation]: "internal",
  [InternalErrors.Unavailable]: "unavailable",
  [InternalErrors.ProviderRequestFailed]: "unavailable",
  [InternalErrors.InvariantViolation]: "internal",
  [InternalErrors.UnknownEntityType]: "validation",
  [InternalErrors.WrongAtomKind]: "validation",
  [InternalErrors.Other]: "internal",
  [InternalErrors.AtomHasNoRelation]: "internal",
  [InternalErrors.Unknown]: "internal",

  [AuthErrors.SignUpRequired]: "auth",
  [AuthErrors.MigrationRequired]: "auth",
  [AuthErrors.EmailNotVerified]: "auth",

  [UserServiceErrors.InviteAlreadyAccepted]: "conflict",
  [UserServiceErrors.EmailAlreadyLinkedToDifferentAuthProvider]: "conflict",
  [UserServiceErrors.EmailLinkRequiresVerifiedMatchingProviderEmail]: "auth",
  [UserServiceErrors.OperationNotSupported]: "internal",

  [PrismaErrors.StoredProcedureNotFound]: "internal",
};

/**
 * The `extensions.code` values of Apollo Server and of Nest's HTTP exception
 * transform. They only decide the class of an error without a framework code.
 * The two persisted-query codes are part of that protocol, not defects.
 */
const CLASS_BY_TRANSPORT_CODE: Record<string, ErrorClass> = {
  UNAUTHENTICATED: "auth",
  FORBIDDEN: "permission",
  BAD_USER_INPUT: "validation",
  BAD_REQUEST: "validation",
  GRAPHQL_PARSE_FAILED: "validation",
  GRAPHQL_VALIDATION_FAILED: "validation",
  OPERATION_RESOLUTION_FAILURE: "validation",
  PERSISTED_QUERY_NOT_FOUND: "validation",
  PERSISTED_QUERY_NOT_SUPPORTED: "validation",
  INTERNAL_SERVER_ERROR: "internal",
};

const CLASS_BY_HTTP_STATUS: Record<number, ErrorClass> = {
  400: "validation",
  401: "auth",
  403: "permission",
  404: "not-found",
  409: "conflict",
  422: "validation",
  429: "busy",
  502: "unavailable",
  503: "unavailable",
  504: "unavailable",
};

const classByCode = new Map<string, ErrorClass>([
  ...Object.entries(CLASS_BY_CATALOG_CODE),
  [VALIDATION_ERROR_CODE, "validation"],
]);
const classByTransportCode = new Map<string, ErrorClass>(
  Object.entries(CLASS_BY_TRANSPORT_CODE),
);

/** The class of an HTTP exception, by its status. */
export function getErrorClassForHttpStatus(status: number): ErrorClass {
  return CLASS_BY_HTTP_STATUS[status] ?? "internal";
}

export type ErrorClassInput = Readonly<{
  /** `debugInfos.errorCode` of a structured error. */
  errorCode?: string;
  /** `type` of a structured error. */
  errorType?: string;
  /** `extensions.code` of Apollo Server or Nest. */
  transportCode?: string;
}>;

/**
 * The class of an error: by its code, then by its type, then by the code of
 * the transport. Everything unknown is `internal`, which includes the codes a
 * project defines itself.
 *
 * Never `rule`: no code, type or transport code has that class. It belongs to
 * a `UserFacingError` alone, which the backend recognises by the error
 * object (`isUserFacingError`), so nothing a client sends can become one.
 */
export function getErrorClass(input: ErrorClassInput): ErrorClass {
  const { errorCode, errorType, transportCode } = input;
  return (
    (errorCode === undefined ? undefined : classByCode.get(errorCode)) ??
    (errorType === undefined ? undefined : classByCode.get(errorType)) ??
    (transportCode === undefined
      ? undefined
      : classByTransportCode.get(transportCode)) ??
    "internal"
  );
}

/**
 * `rule` is among them: a rejected rule of a project is an answer to what the
 * user did, not a defect. So is `auth`: a missing, expired or invalid
 * credential (the 401 / `UNAUTHENTICATED` of an auth guard) and an `AuthErrors`
 * code the user acts on are the client's.
 */
const CLIENT_CAUSED_ERROR_CLASSES: ReadonlySet<ErrorClass> = new Set([
  "permission",
  "validation",
  "not-found",
  "rule",
  "auth",
]);

/**
 * Whether the caller caused an error of this class. Such an error of a
 * request is a warning in the backend log and is not reported to error
 * tracking.
 *
 * The class alone decides: no code is thrown for a mistake of the caller and
 * for a defect alike. A defect is thrown with a code of the class `internal`
 * (`InternalErrors.InvariantViolation`, `InternalErrors.Configuration`),
 * never with `InternalErrors.Validation` or `InternalErrors.NotFound`. The one
 * exception: a commit that writes a child before the parent it creates gets
 * `NotFound`, like a parent that does not exist (errors/README.md, "An
 * unknown parent"). A missing acting user in backend code that runs behind
 * the auth guard is a defect: `InternalErrors.NoActingUser` is `internal`,
 * and an auth module rejects a request without a user with a 401.
 */
export function isClientCausedErrorClass(errorClass: ErrorClass): boolean {
  return CLIENT_CAUSED_ERROR_CLASSES.has(errorClass);
}
